import React from 'react';
import { useAuthStore } from '../store';
import { movieApi } from '../api/client';
import toast from 'react-hot-toast';

const MyListContext = React.createContext({
  list: [],
  isLoading: false,
  isInList: () => false,
  addToList: async () => {},
  removeFromList: async () => {},
  toggleList: async () => {},
  refreshList: async () => {},
});

const GUEST_STORAGE_KEY = 'cast_guest_my_list';

export function MyListProvider({ children }) {
  const token = useAuthStore((s) => s.token);
  const [list, setList] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);

  // Load from local storage or server
  const refreshList = React.useCallback(async () => {
    if (token) {
      try {
        setIsLoading(true);
        const res = await movieApi.getMyList();
        const items = res.data?.data || res.data || [];
        setList(Array.isArray(items) ? items : []);
      } catch (err) {
        console.error('Failed to load My List from server:', err);
      } finally {
        setIsLoading(false);
      }
    } else {
      try {
        const stored = localStorage.getItem(GUEST_STORAGE_KEY);
        setList(stored ? JSON.parse(stored) : []);
      } catch {
        setList([]);
      }
    }
  }, [token]);

  React.useEffect(() => {
    refreshList();
  }, [refreshList]);

  const isInList = React.useCallback(
    (id) => {
      const matchId = String(id);
      return list.some((item) => String(item.id || item.Id || item.MovieId) === matchId);
    },
    [list]
  );

  const addToList = React.useCallback(
    async (title) => {
      const id = title.id || title.Id || title.MovieId;
      if (!id) return;

      if (token) {
        try {
          await movieApi.addToMyList(id);
          toast.success(`Added "${title.title || title.Title || 'Title'}" to My List`);
          refreshList();
        } catch (err) {
          toast.error('Could not add to My List');
        }
      } else {
        const updated = [...list, title];
        setList(updated);
        localStorage.setItem(GUEST_STORAGE_KEY, JSON.stringify(updated));
        toast.success(`Added "${title.title || title.Title || 'Title'}" to My List`);
      }
    },
    [token, list, refreshList]
  );

  const removeFromList = React.useCallback(
    async (id) => {
      if (!id) return;
      const matchId = String(id);

      if (token) {
        try {
          await movieApi.removeFromMyList(id);
          toast.success('Removed from My List');
          refreshList();
        } catch (err) {
          toast.error('Could not remove from My List');
        }
      } else {
        const updated = list.filter((item) => String(item.id || item.Id || item.MovieId) !== matchId);
        setList(updated);
        localStorage.setItem(GUEST_STORAGE_KEY, JSON.stringify(updated));
        toast.success('Removed from My List');
      }
    },
    [token, list, refreshList]
  );

  const toggleList = React.useCallback(
    async (title) => {
      const id = title.id || title.Id || title.MovieId;
      if (isInList(id)) {
        await removeFromList(id);
      } else {
        await addToList(title);
      }
    },
    [isInList, addToList, removeFromList]
  );

  return (
    <MyListContext.Provider
      value={{
        list,
        isLoading,
        isInList,
        addToList,
        removeFromList,
        toggleList,
        refreshList,
      }}
    >
      {children}
    </MyListContext.Provider>
  );
}

export function useMyList() {
  return React.useContext(MyListContext);
}
